import { useSearchParams } from "react-router-dom";
import {
  useGetStudentDashboardApplicationStatusQuery,
  useGetStudentDashboardApplicationStatusViewOnlyQuery
} from "../../services/graphql/api";
import useUserEmplid from "./useUserEmplid";
import { isTestScenariosEnabled, shouldUseMockData } from "../../services/mock/test-scenarios";
import useMockData from "./useMockData";

/**
 * Custom hook for managing Student Dashboard data fetching
 * Handles both student and admin modes with appropriate queries
 * Cleanly separates mock data from real API calls
 */
export const useStudentDashboardData = () => {
  // Get user authentication data (may be overridden in admin mode)
  const { emplid: userEmplid, isAuthenticated, isLoading: authLoading, isAdminOverride } = useUserEmplid();

  // For development: allow scenario override with mock data
  const [searchParams] = useSearchParams();
  const devScenario = searchParams.get("scenario");
  const isDevelopment = isTestScenariosEnabled();
  const useMockDataFlag = shouldUseMockData(devScenario);

  // Mock data hook - bypasses API completely
  const mockDataResult = useMockData(devScenario);

  // Real API query setup
  const shouldSkip = !isAuthenticated || !userEmplid || authLoading || useMockDataFlag;

  // Regular student query (uses getSelfService with user token)
  const studentQuery = useGetStudentDashboardApplicationStatusQuery(userEmplid, {
    skip: shouldSkip || isAdminOverride
  });

  // Admin view query (uses getSelfServiceViewOnly with specific emplid)
  const adminQuery = useGetStudentDashboardApplicationStatusViewOnlyQuery(userEmplid, {
    skip: shouldSkip || !isAdminOverride
  });

  // Choose the appropriate data source
  let queryResult;
  if (useMockDataFlag) {
    queryResult = mockDataResult;
  } else {
    queryResult = isAdminOverride ? adminQuery : studentQuery;
  }

  return {
    ...queryResult,
    // Additional computed values
    isDevelopment,
    devScenario,
    userEmplid,
    isAuthenticated,
    authLoading,
    isAdminOverride,
    useMockData: useMockDataFlag
  };
};

export default useStudentDashboardData;